import React, { useCallback, useState } from "react";
import { api, extractApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, UserCircle2 } from "lucide-react";
import { useLanguage, useT } from "../lib/LanguageContext";
// EVERY import relative, on purpose (the reviews/affiliate convention): there is no "@"
// alias on the Jest side, so a relative specifier is what lets a test mock the REAL module.
import useAdminLoad, { payloadShapeError } from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";
import SpecialtiesEditor from "./branding/SpecialtiesEditor";
import { SPECIALTIES, COUNTRIES, resolveSpecialty, resolveCountry } from "../i18n/practiceTaxonomy";
import PractitionerAbout from "../public/PractitionerAbout";
import { SPOKEN_LANGUAGE_CODES, languageName } from "../public/profileFacts";
// crm-module BEGIN C3.02/C3.04 (lane F4)
import PromoteButton from "./crm/promote/PromoteButton";
// crm-module END

// Must stay in step with `tenant_self_service` (MAX_TAGLINE_LEN, MAX_BIO_LEN). The server
// is the authority and refuses anything longer; these only let the form say so first.
export const HEADLINE_MAX = 160;
export const BIO_MAX = 2000;

const EMPTY = {
  tagline: "", specialties: [], specialty: "", bio: "", country: "", photo_url: "", languages: [],
};

/** The editor's form from the API's profile view — every key present, strings never null. */
const toForm = (data) => ({
  ...EMPTY,
  ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, data?.[k] ?? EMPTY[k]])),
});

const isProfile = (d) => d && typeof d === "object" && Array.isArray(d.languages)
  && Array.isArray(d.specialties) && typeof d.bio === "string";

// A stored value outside the catalogue (free text from another writer) still needs an option
// to sit on — the same rule the onboarding wizard applies to the same two fields.
const withCurrentValue = (options, resolved) =>
  resolved.labelKey || !resolved.text
    ? options
    : [...options, { code: resolved.text, stored: resolved.text, labelKey: null }];

/**
 * PublicProfileAdmin — `/admin/profile`: the ONE place a practitioner says, in their own
 * words, who they are on the marketplace.
 *
 * WHY A SCREEN OF ITS OWN. Before it, the description, country and photo could be written
 * once — by the onboarding wizard — and never changed; the headline and specialty chips were
 * on the Branding screen, between the colour pickers. The marketplace's "Get to know" block
 * reads all of them, so they are edited together here, saved by ONE request
 * (`PUT /api/tenant/profile`) so half a profile can never save, and shown in a LIVE preview
 * rendered by the very component the public page uses.
 *
 * Every write handler catches (silentWriteHandlers), and a refusal is shown through
 * `extractApiError`, never by rendering the response body (apiErrorHygiene).
 */
export default function PublicProfileAdmin() {
  const t = useT("admin");
  const tm = useT("marketplace");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [form, setForm] = useState(null);
  const [tenant, setTenant] = useState(null);
  // Read-only, from the profile read: the same fallback the marketplace page applies.
  const [memberSince, setMemberSince] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [profile, info] = await Promise.all([
      api.get("/tenant/profile"),
      api.get("/tenant/self/info"),
    ]);
    // A 200 that is not a profile (the production proxy's index.html, a degraded answer)
    // must not become an empty form that a Save would then write over the real profile.
    if (!isProfile(profile.data)) throw payloadShapeError();
    setForm(toForm(profile.data));
    setMemberSince(profile.data.member_since || null);
    setTenant(info.data || {});
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/tenant/profile", form);
      if (isProfile(data)) setForm(toForm(data));
      toast.success(t("pp.saved"));
    } catch (e) {
      toast.error(extractApiError(e, t("pp.saveError"), tv));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data?.url) {
        set({ photo_url: data.url });
        toast.success(t("pp.photo.uploaded"));
      } else {
        toast.error(t("pp.photo.uploadError"));
      }
    } catch (err) {
      toast.error(extractApiError(err, t("pp.photo.uploadError"), tv));
    } finally {
      setUploading(false);
    }
  };

  const toggleLanguage = (code) =>
    set({
      languages: form.languages.includes(code)
        ? form.languages.filter((c) => c !== code)
        : [...form.languages, code],
    });

  if (loadError) {
    return (
      <div data-testid="public-profile-admin" className="max-w-5xl">
        <LoadFailure
          message={extractApiError(loadError, tv("errors.loadFailed"), tv)}
          onRetry={reload}
          retryLabel={tv("actions.retry")}
          testId="public-profile-load-error"
          retryTestId="public-profile-retry"
        />
      </div>
    );
  }
  if (!form) return <Loader2 className="w-6 h-6 animate-spin text-eh-muted" />;

  // Offer every suggested language, then any already-saved code the list does not carry,
  // so a saved value is never invisible (and so never silently dropped by the next save).
  const languageOptions = [
    ...SPOKEN_LANGUAGE_CODES,
    ...form.languages.filter((c) => !SPOKEN_LANGUAGE_CODES.includes(c)),
  ];
  const specialty = resolveSpecialty(form.specialty);
  const country = resolveCountry(form.country);
  const preview = {
    ...form,
    name: tenant?.name || "",
    logo_url: tenant?.branding?.logo_url || "",
    member_since: memberSince,
  };

  return (
    <div data-testid="public-profile-admin" className="space-y-8 max-w-6xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
            <UserCircle2 className="w-6 h-6 text-sol-gold" /> {t("pp.title")}
          </h1>
          <p className="text-eh-muted text-sm mt-1 max-w-xl">{t("pp.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {tenant?.slug && (
            <a
              href={`/marketplace/${encodeURIComponent(tenant.slug)}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-eh-ink underline underline-offset-4"
              data-testid="public-profile-view"
            >
              {t("pp.viewPublic")}
            </a>
          )}
          {/* crm-module BEGIN C3.02/C3.04 — Promote (lane F4). The profile itself is
              always "saved" (it is the signed-in tenant); no `editing.id` gate needed. */}
          {tenant?.tenant_id && (
            <PromoteButton itemType="profile" itemId={tenant.tenant_id} itemName={tenant?.name} thumbnailUrl={tenant?.branding?.logo_url} />
          )}
          {/* crm-module END */}
          <Button onClick={save} disabled={saving || uploading} data-testid="public-profile-save" className="bg-eh-ink text-eh-bg">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {t("pp.save")}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-6">
            {/* PHOTO */}
            <div>
              <Label className="text-xs">{t("pp.photo.label")}</Label>
              <div className="flex items-center gap-4 mt-2">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="" className="w-16 h-16 rounded-full object-cover border border-eh-border-soft" data-testid="public-profile-photo" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-eh-cream/60 border border-eh-border-soft" aria-hidden="true" />
                )}
                <label className="inline-flex items-center h-9 px-3 rounded-md border border-eh-border-soft text-sm cursor-pointer hover:bg-eh-cream/40">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {t("pp.photo.upload")}
                  <input type="file" accept="image/*" className="sr-only" onChange={uploadPhoto} data-testid="public-profile-photo-input" />
                </label>
                {form.photo_url && (
                  <button type="button" onClick={() => set({ photo_url: "" })} className="text-sm text-eh-muted underline" data-testid="public-profile-photo-remove">
                    {t("pp.photo.remove")}
                  </button>
                )}
              </div>
              <p className="text-xs text-eh-muted mt-2">{t("pp.photo.hint")}</p>
            </div>

            {/* HEADLINE */}
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="pp-headline" className="text-xs">{t("pp.headline.label")}</Label>
                <span className="text-[11px] text-eh-muted tabular-nums">{t("pp.counter", { n: form.tagline.length, max: HEADLINE_MAX })}</span>
              </div>
              <Input
                id="pp-headline"
                value={form.tagline}
                maxLength={HEADLINE_MAX}
                onChange={(e) => set({ tagline: e.target.value })}
                data-testid="public-profile-headline"
                className="mt-1.5"
              />
              <p className="text-xs text-eh-muted mt-1.5">{t("pp.headline.hint")}</p>
            </div>

            {/* DESCRIPTION */}
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="pp-bio" className="text-xs">{t("pp.bio.label")}</Label>
                <span className="text-[11px] text-eh-muted tabular-nums" data-testid="public-profile-bio-counter">
                  {t("pp.counter", { n: form.bio.length, max: BIO_MAX })}
                </span>
              </div>
              <textarea
                id="pp-bio"
                value={form.bio}
                maxLength={BIO_MAX}
                rows={8}
                onChange={(e) => set({ bio: e.target.value })}
                data-testid="public-profile-bio"
                className="w-full mt-1.5 border border-eh-border-soft rounded-md p-3 text-sm leading-relaxed"
              />
              <p className="text-xs text-eh-muted mt-1.5">{t("pp.bio.hint")}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-6">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="pp-specialty" className="text-xs">{t("pp.specialty.label")}</Label>
                <select
                  id="pp-specialty"
                  value={form.specialty}
                  onChange={(e) => set({ specialty: e.target.value })}
                  data-testid="public-profile-specialty"
                  className="w-full mt-1.5 border border-eh-border-soft rounded-md px-3 h-10 text-sm bg-white"
                >
                  <option value="">{t("pp.specialty.none")}</option>
                  {withCurrentValue(SPECIALTIES, specialty).map((s) => (
                    <option key={s.stored} value={s.stored}>{s.labelKey ? t(s.labelKey) : s.stored}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="pp-country" className="text-xs">{t("pp.country.label")}</Label>
                <select
                  id="pp-country"
                  value={form.country}
                  onChange={(e) => set({ country: e.target.value })}
                  data-testid="public-profile-country"
                  className="w-full mt-1.5 border border-eh-border-soft rounded-md px-3 h-10 text-sm bg-white"
                >
                  <option value="">{t("pp.country.none")}</option>
                  {withCurrentValue(COUNTRIES, country).map((c) => (
                    <option key={c.stored} value={c.stored}>{c.labelKey ? t(c.labelKey) : c.stored}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">{t("pp.specialties.label")}</Label>
              <SpecialtiesEditor value={form.specialties} onChange={(specialties) => set({ specialties })} />
            </div>

            <fieldset className="m-0 p-0 border-0 min-w-0">
              <legend className="text-xs font-medium">{t("pp.languages.label")}</legend>
              <div className="flex flex-wrap gap-2 mt-2">
                {languageOptions.map((code) => {
                  const on = form.languages.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleLanguage(code)}
                      data-testid={`public-profile-lang-${code}`}
                      className={`px-3 py-1.5 rounded-full border text-sm capitalize transition ${
                        on ? "bg-sol-midnight text-white border-sol-midnight" : "border-eh-border-soft hover:bg-eh-cream/40"
                      }`}
                    >
                      {languageName(code, lang)}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-eh-muted mt-2">{t("pp.languages.hint")}</p>
            </fieldset>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-6">
            <p className="text-sm font-medium text-eh-ink">{t("pp.preview.title")}</p>
            <p className="text-xs text-eh-muted mb-3">{t("pp.preview.hint")}</p>
            <div className="rounded-2xl bg-sol-midnight text-white p-6" data-testid="public-profile-preview">
              <PractitionerAbout profile={preview} t={tm} lang={lang} preview />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
